import { useState, useEffect, useMemo } from "react"
import CustomButton from "../../../components/CustomButton/Button"
import { ArrowLeftOutlined, ArrowDownOutlined } from "@ant-design/icons"
import {
    BTN_TXT,
    TITLE_TXT,
    DEAL_STATUS_IDS,
    DEAL_DETAIL_TABS,
    DEAL_DETAIL_FUNDING_VISIBLE_KEYS,
    DEAL_DETAIL_SETTLEMENT_VISIBLE_KEYS,
    DEAL_DETAIL_PAY_NOW_SELECTED_STATUSES,
    DEAL_DETAIL_PAY_NOW_DEAL_STATUSES,
    DEAL_DETAIL_DOWNLOAD_FALLBACK,
    DEAL_DETAIL_MODAL_WIDTH,
} from "../../../constants/constants"
import { useNavigate, useLocation } from "react-router-dom"
import { Segmented, Dropdown, Button, Row, Col, Flex, App } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { dealDetailedView } from "../../../redux/slices/DealSlice"
import Loader from "../../../components/CustomLoader/Loader"
import DealDetails from "./DealDetails"
import DealLevelFunding from "./DealLevelFunding"
import DealLevelSettlement from "./DealLevelSettlement"
import CustomModal from "../../../components/CustomModal/CustomModal"
import AccountDetails from "../../../components/AccountDetails/AccountDetails"
import { isSupplyChainCategory } from "../../../helpers/dealCategoryProducts";
import { getDownloadSuccessMessage } from "../../../helpers/helpers"

const DealDetailView = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { dealId, selectedStatus, dealStatus } = location.state || {};
    const { detailedview, loading } = useSelector((state) => state?.deals);
    const { message } = App.useApp();
    const [selectedTab, setSelectedTab] = useState(DEAL_DETAIL_TABS.DETAILS);
    const [escrowDetailsOpen, setEscrowDetailsOpen] = useState(false);

    useEffect(() => {
        dispatch(dealDetailedView(dealId));
    }, [dispatch, dealId])

    const dealSegments = useMemo(() => {
        const status = detailedview?.dealLevelSummary?.dealStatus || dealStatus;
        const segments = [
            {
                label: TITLE_TXT.DEAL_DETS,
                value: DEAL_DETAIL_TABS.DETAILS,
            },
        ];
        if (DEAL_DETAIL_FUNDING_VISIBLE_KEYS.has(status)) {
            segments.push({
                label: TITLE_TXT.DEAL_LEVEL_FUND,
                value: DEAL_DETAIL_TABS.FUNDING,
            });
        }
        if (DEAL_DETAIL_SETTLEMENT_VISIBLE_KEYS.has(status)) {
            segments.push({
                label: TITLE_TXT.DEAL_LEVEL_SETTLE,
                value: DEAL_DETAIL_TABS.SETTLEMENT,
            });
        }
        return segments;
    }, [dealStatus, detailedview?.dealLevelSummary?.dealStatus]);

    useEffect(() => {
        const allowed = new Set(dealSegments.map((s) => s.value));
        if (!allowed.has(selectedTab)) {
            setSelectedTab(DEAL_DETAIL_TABS.DETAILS);
        }
    }, [dealSegments, selectedTab]);

    const downloadInvoices = (fileUrl, fallbackName = DEAL_DETAIL_DOWNLOAD_FALLBACK) => {
        if (!fileUrl) return;
        const downloadedFileName = fileUrl.split("/").pop()?.split("?")[0] || fallbackName;
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = downloadedFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success(getDownloadSuccessMessage(downloadedFileName));
    };

    const summaryStatus = detailedview?.dealLevelSummary?.dealStatus;
    const summaryStatusId = detailedview?.dealLevelSummary?.dealStatusId;
    const showPayNow =
        (DEAL_DETAIL_PAY_NOW_SELECTED_STATUSES.has(selectedStatus) ||
            DEAL_DETAIL_PAY_NOW_DEAL_STATUSES.has(dealStatus)) &&
        (DEAL_DETAIL_PAY_NOW_DEAL_STATUSES.has(summaryStatus) ||
            summaryStatusId === DEAL_STATUS_IDS.PARTIALLY_SETTLED);

    return (
        <div className="deal-details-container">
            <CustomButton
                variant="text"
                color="primary"
                icon={<ArrowLeftOutlined />}
                iconPosition="start"
                onClick={() => navigate(-1)}
                style={{ color: "#40566D", fontSize: 14, padding: 5 }}
            >
                {BTN_TXT.BACK}
            </CustomButton>
            <Row gutter={[0, 16]} align="middle" justify="space-between" style={{ width: "100%", marginTop: 20 }}>
                <Col>
                    <div className="segmented-wrapper">
                        <Segmented
                            options={dealSegments}
                            value={selectedTab}
                            onChange={(value) => setSelectedTab(value)}
                            className="custom-segmented"
                        />
                    </div>
                </Col>
                <Col>
                    <Flex gap={20}>
                        {showPayNow && (
                            <CustomButton
                                variant="solid"
                                color="primary"
                                style={{ padding: 24 }}
                                onClick={() => {
                                    setEscrowDetailsOpen(true);
                                }}
                            >
                                {BTN_TXT.PAY_NOW}
                            </CustomButton>
                        )}
                        <CustomModal
                            open={escrowDetailsOpen}
                            centered
                            onCancel={() => setEscrowDetailsOpen(false)}
                            content={
                                <AccountDetails
                                    title={TITLE_TXT.BUS_ENT_ESCW_ACC_DETS}
                                    details={detailedview?.busEntEscrow?.[0]}
                                />
                            }
                            width={DEAL_DETAIL_MODAL_WIDTH}
                        />
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "1",
                                        label: (
                                            <span onClick={() => downloadInvoices(detailedview?.dealLevelSummary?.downloadUrl?.downloadInvoiceSummary)} style={{ margin: 10 }}>
                                                {isSupplyChainCategory(
                                                    detailedview?.dealLevelSummary?.dealCategoryId ||
                                                    detailedview?.dealLevelSummary?.dealType ||
                                                    location?.state?.dealCategoryId
                                                ) ? (BTN_TXT.DWNLD_PO_CSV || "Download Purchase Order CSV") : BTN_TXT.DWNLD_INV_CSV}
                                            </span>
                                        ),
                                    },
                                    { type: "divider" },
                                    {
                                        key: "2",
                                        label: (
                                            <span onClick={() => downloadInvoices(detailedview?.dealLevelSummary?.downloadUrl?.downloadInvoiceFile)} style={{ margin: 10 }}>
                                                {isSupplyChainCategory(
                                                    detailedview?.dealLevelSummary?.dealCategoryId ||
                                                    detailedview?.dealLevelSummary?.dealType ||
                                                    location?.state?.dealCategoryId
                                                ) ? (BTN_TXT.DWNLD_PO_ZIP || "Download Purchase Order Zip") : BTN_TXT.DWNLD_INV_ZIP}
                                            </span>
                                        ),
                                    },
                                ],
                            }}
                            trigger={["click"]}
                            overlayStyle={{
                                border: "1px solid #d9d9d9",
                                borderRadius: "8px",
                                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                        >
                            <Button
                                variant="solid"
                                color="primary"
                                icon={<ArrowDownOutlined />}
                                iconPosition="end"
                                style={{ padding: 24 }}
                            >
                                {BTN_TXT.DOWNLOAD}
                            </Button>
                        </Dropdown>
                    </Flex>
                </Col>
            </Row>
            {loading ? <Loader /> : (
                <>
                    {selectedTab === DEAL_DETAIL_TABS.DETAILS && (
                        <DealDetails deal={detailedview?.dealLevelSummary} />
                    )}
                    {selectedTab === DEAL_DETAIL_TABS.FUNDING && (
                        <DealLevelFunding deal={detailedview?.dealLevelFunding} status={detailedview?.dealLevelSummary?.dealStatus} dealId={dealId} />
                    )}
                    {selectedTab === DEAL_DETAIL_TABS.SETTLEMENT && (
                        <DealLevelSettlement deal={detailedview?.dealLevelSettlement} status={selectedStatus} />
                    )}
                </>
            )}
        </div>
    )
}

export default DealDetailView
